import { useLazyQuery } from '@apollo/client';
import { IntegrationState } from '@interfaces/integrations';
import { NavigationStyles } from '@interfaces/role';
import type React from 'react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { translateBackend } from '../assets/i18n/config';
import { CONFIG } from '../config';
import { FETCH_INTEGRATIONS } from '../pages/devSettings/components/integrations.operations';
import {
  initAnalytics,
  sendAnalyticsEvent,
} from '../utils/analytics/analytics';
import {
  type AnalyticsProviderType,
  AnalyticsProviderTypes,
} from '../utils/analytics/providerTypes';
import type { AnalyticsMeta, AnalyticsSendFn } from '../utils/analytics/types';
import { UserContext, usePermissions } from './userContextProvider';

export type { AnalyticsProviderType } from '../utils/analytics/providerTypes';
export { AnalyticsProviderTypes } from '../utils/analytics/providerTypes';

type AnalyticsContextType = {
  sendAnalytic: AnalyticsSendFn;
};

const noopSendAnalytic: AnalyticsSendFn = () => undefined;

const firebaseDefaultConfig = {
  apiKey: CONFIG.analytics.apiKey ?? '',
  authDomain: CONFIG.analytics.authDomain ?? '',
  projectId: CONFIG.analytics.projectId ?? '',
  storageBucket: CONFIG.analytics.storageBucket ?? '',
  messagingSenderId: CONFIG.analytics.messagingSenderId ?? '',
  appId: CONFIG.analytics.appId ?? '',
  measurementId: CONFIG.analytics.measurementId ?? '',
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  sendAnalytic: noopSendAnalytic,
});
const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { activeOrganization, userContext, activeEntity } =
    useContext(UserContext);
  const [provider, setProvider] = useState<AnalyticsProviderType[]>([]);
  const [lastSendEvent, setLastSentEvent] = useState<{
    event: string;
    timestamp: number;
  }>({ event: '', timestamp: 0 });
  const { permissions } = usePermissions();
  const getDefaultFirebase = () => {
    try {
      initAnalytics({
        provider: AnalyticsProviderTypes.GOOGLE,
        providerId: 'onevest-default',
        configType: firebaseDefaultConfig,
      });
    } catch {
      /* empty */
    }
  };
  const getDefaultMixPanel = () => {
    try {
      initAnalytics({
        provider: AnalyticsProviderTypes.MIXPANEL,
        providerId: 'onevest-default',
        configType: {
          projectToken: CONFIG.analytics.mixPanelProjectToken ?? '',
        },
        user: {
          id: activeEntity?.id ?? userContext.id,
          organizationName: translateBackend(
            userContext.organization?.translatedName,
          ),
          organizationId: activeOrganization.id,
          userRole: translateBackend(userContext.role?.translatedName),
        },
      });
    } catch {
      /* empty */
    }
  };
  const sendAnalytic = useCallback<AnalyticsSendFn>(
    (event, meta) => {
      const now = Date.now();
      const timeSinceLastEvent = now - lastSendEvent.timestamp;

      // Only prevent duplicate events if they're the same event AND happened within 100ms
      // This allows legitimate repeated interactions while preventing rapid-fire duplicates
      if (event === lastSendEvent.event && timeSinceLastEvent < 100) return;

      const mergedMeta: AnalyticsMeta = {
        ...(meta ?? {}),
        subdomainName: userContext.organization?.subdomain,
        organizationName: translateBackend(
          userContext.organization?.translatedName,
        ),
        organizationId: activeOrganization.id,
        userRole: translateBackend(userContext.role?.translatedName),
        orgName: userContext.organization?.translatedName?.en,
        orgId: activeOrganization.id,
        role: userContext.role?.translatedName?.en,
        roleProfile: userContext.role?.publicRoleProfile?.translatedName?.en,
      };
      sendAnalyticsEvent(provider, event, mergedMeta);
      setLastSentEvent({ event, timestamp: now });
    },
    [
      activeOrganization.id,
      lastSendEvent.event,
      lastSendEvent.timestamp,
      provider,
      userContext.organization?.translatedName?.en,
      userContext.organization?.subdomain,
      userContext.role?.translatedName?.en,
      userContext.role?.publicRoleProfile?.translatedName?.en,
    ],
  );
  const [fetchIntegration] = useLazyQuery(FETCH_INTEGRATIONS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          type: 'ANALYTICS',
          state: IntegrationState.ACTIVE,
        },
      },
    },
    onCompleted: (response): void => {
      const mixpanelToken = new Set<string>();
      const fetchedIntegrations =
        response.fetchIntegrations.integrations.filter((integration: any) => {
          if (integration.provider === AnalyticsProviderTypes.MIXPANEL) {
            if (!mixpanelToken.has(integration.configuration.TBD)) {
              mixpanelToken.add(integration.configuration.TBD);
              return true;
            }
            return false;
          }
          return true;
        });

      // Check if there's a custom Mixpanel integration to avoid duplicate events
      const hasCustomMixpanel = fetchedIntegrations.some(
        (i: any) => i.provider === AnalyticsProviderTypes.MIXPANEL,
      );

      // Build providers list, avoiding duplicate Mixpanel providers
      const analyticsIntegrations: AnalyticsProviderType[] = [
        { type: AnalyticsProviderTypes.GOOGLE, id: 'onevest-default' },
      ];

      // Only add default Mixpanel if no custom Mixpanel integration exists
      if (!hasCustomMixpanel) {
        analyticsIntegrations.push({
          type: AnalyticsProviderTypes.MIXPANEL,
          id: 'onevest-default',
        });
      }

      // Add all custom integrations from backend
      fetchedIntegrations.forEach((integration: any) => {
        analyticsIntegrations.push({
          type: integration.provider,
          id: integration.id,
        });
      });

      setProvider(analyticsIntegrations);

      getDefaultFirebase();
      if (!hasCustomMixpanel) {
        getDefaultMixPanel();
      }

      // Initialize custom integrations
      fetchedIntegrations.forEach((integration: any): void => {
        if (integration.provider === AnalyticsProviderTypes.GOOGLE) {
          try {
            initAnalytics({
              provider: AnalyticsProviderTypes.GOOGLE,
              providerId: integration.id,
              configType: {
                apiKey: integration.configuration.apiKey,
                authDomain: integration.configuration.authDomain,
                projectId: integration.configuration.projectId,
                storageBucket: integration.configuration.storageBucket,
                messagingSenderId: integration.configuration.messagingSenderId,
                appId: integration.configuration.appId,
                measurementId: integration.configuration.measurementId,
              },
            });
          } catch {
            /* empty */
          }
        }
        if (integration.provider === AnalyticsProviderTypes.MIXPANEL) {
          initAnalytics({
            provider: AnalyticsProviderTypes.MIXPANEL,
            providerId: integration.id,
            configType: {
              projectToken: integration.configuration.TBD,
            },
            user: {
              id: activeEntity?.id ?? userContext.id,
              organizationName: translateBackend(
                userContext.organization?.translatedName,
              ),
              organizationId: activeOrganization.id,
              userRole: translateBackend(userContext.role?.translatedName),
            },
          });
        }
      });
    },
    onError: (): void => {
      setDefaultProviders();
    },
  });

  const setDefaultProviders = () => {
    getDefaultFirebase();
    getDefaultMixPanel();
    setProvider([
      { type: AnalyticsProviderTypes.GOOGLE, id: 'onevest-default' },
      { type: AnalyticsProviderTypes.MIXPANEL, id: 'onevest-default' },
    ]);
  };

  useEffect(() => {
    const hasUserContext =
      userContext && Object.entries(userContext).length > 0;
    const isUnifiedClientExperience =
      userContext.role?.navigationStyle === NavigationStyles.SIMPLE;
    if (
      (hasUserContext && isUnifiedClientExperience && activeEntity) ||
      (!isUnifiedClientExperience && hasUserContext)
    ) {
      if (permissions.includes('read:integrations')) {
        fetchIntegration().then();
      } else {
        setDefaultProviders();
      }
    }
  }, [fetchIntegration, activeEntity, userContext]);

  return (
    <AnalyticsContext.Provider value={{ sendAnalytic }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export { AnalyticsContext, AnalyticsProvider };
